import React, { useEffect, useState } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// ✅ Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// === Color schema ===
const GREEN = "#2e7d32";   // VIP
const ORANGE = "#fb8c00";  // Średni
const BLUE = "#2196f3";    // Mali

const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  padding: 15px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const Select = styled.select`
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid #ccc;
`;

const CenteredChart = styled.div`
  width: 100%;
  max-width: 400px;
  height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
`;

const CustomerSegmentation = ({ salesData }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [totalCustomers, setTotalCustomers] = useState(0);

  const monthNames = [
    "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
    "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"
  ];

  const calculateTransactionValue = (row) =>
    parseFloat(row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22) || 0;

  useEffect(() => {
    if (!salesData || salesData.length === 0) return;
    const years = [...new Set(salesData.map((sale) => parseInt(sale.Rok, 10)))].sort((a, b) => b - a);
    setAvailableYears(years);
    if (!years.includes(selectedYear)) setSelectedYear(years[0]);
  }, [salesData]);

  useEffect(() => {
    if (selectedYear) {
      const months = [...new Set(
        salesData
          .filter((sale) => parseInt(sale.Rok, 10) === selectedYear)
          .map((sale) => parseInt(sale.Miesiac, 10))
      )].sort((a, b) => a - b);
      setAvailableMonths(months);
    }
  }, [selectedYear, salesData]);

  useEffect(() => {
    if (!selectedYear) return;
    const filteredData = salesData.filter(
      (sale) =>
        parseInt(sale.Rok, 10) === selectedYear &&
        (selectedMonth === "all" || parseInt(sale.Miesiac, 10) === selectedMonth)
    );

    if (filteredData.length === 0) {
      setChartData(null);
      setTotalCustomers(0);
      return;
    }

    const customerTotals = filteredData.reduce((acc, sale) => {
      const contractor = sale.NazwaKontrahenta || "Nieznany Kontrahent";
      acc[contractor] = (acc[contractor] || 0) + calculateTransactionValue(sale);
      return acc;
    }, {});

    const totalsSorted = Object.values(customerTotals).sort((a, b) => b - a);
    if (totalsSorted.length < 3) {
      setChartData(null);
      setTotalCustomers(0);
      return;
    }

    const vipThreshold = totalsSorted[Math.floor(totalsSorted.length * 0.2)];
    const mediumThreshold = totalsSorted[Math.floor(totalsSorted.length * 0.6)];

    let vipCount = 0, mediumCount = 0, smallCount = 0;
    Object.values(customerTotals).forEach((total) => {
      if (total >= vipThreshold) vipCount++;
      else if (total >= mediumThreshold) mediumCount++;
      else smallCount++;
    });

    const total = vipCount + mediumCount + smallCount;
    setTotalCustomers(total);

    setChartData({
      labels: ["VIP Klienci (Top 20%)", "Średni Klienci (40%)", "Mali Klienci (40%)"],
      datasets: [
        {
          label: "Segmentacja klientów",
          data: [vipCount, mediumCount, smallCount],
          backgroundColor: [GREEN, ORANGE, BLUE],
          borderColor: "#ffffff",
          borderWidth: 2,
          hoverOffset: 6,
        },
      ],
    });
  }, [selectedYear, selectedMonth, salesData]);

  return (
    <ChartWrapper>
      <TitleWrapper>
        Segmentacja Klientów
        <div data-tooltip-id="segmentation-tooltip" style={{ cursor: "pointer" }}>
          <Info size={18} />
        </div>
      </TitleWrapper>

      <InfoTooltip
        id="segmentation-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Segmentacja klientów</strong><br/>
            Wykres kołowy pokazuje udział<br/>
            VIP, średnich i małych klientów<br/>
            na podstawie sumy transakcji.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label>Rok:</label>
          <Select value={selectedYear || ""} onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}>
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </div>
        <div>
          <label>Miesiąc:</label>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value === "all" ? "all" : parseInt(e.target.value, 10))}
          >
            <option value="all">Cały rok</option>
            {availableMonths.map((month) => (
              <option key={month} value={month}>{monthNames[month - 1]}</option>
            ))}
          </Select>
        </div>
      </FiltersWrapper>

      {chartData ? (
        <CenteredChart>
          <Pie
            data={chartData}
            plugins={[ChartDataLabels]}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: "top" },
                tooltip: {
                  backgroundColor: "#333",
                  titleColor: "#fff",
                  bodyColor: "#fff",
                },
                datalabels: {
                  color: "#fff",
                  font: { size: 12, weight: "bold" },
                  anchor: "center",
                  align: "center",
                  formatter: (value) =>
                    totalCustomers > 0 ? `${((value / totalCustomers) * 100).toFixed(1)}%` : "",
                },
              },
            }}
          />
        </CenteredChart>
      ) : (
        <p style={{ textAlign: "center" }}>Brak danych dla wybranych filtrów.</p>
      )}
    </ChartWrapper>
  );
};

export default CustomerSegmentation;
